// src/pages/PatientVisitsPage.jsx
// Route: /patient/visits
//
// The patient's signed clinic visits, newest first. Reached from the
// "Visit summaries" tab of My Appointments and from the Today-screen card.
// API: GET /api/patients/{patientId}/visits (SIGNED only).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import PageHead from '../components/PageHead';
import AppointmentsTabs from '../components/AppointmentsTabs';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import { usePatient } from '../context/PatientContext';
import patientVisitApi from '../services/patientVisitApi';
import { buildVisitListModel, isRecentVisit, readSeenVisitIds } from '../utils/visitSummary';

export default function PatientVisitsPage() {
  const { patient } = usePatient();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', visits: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!patient?.id) return undefined;
    let cancelled = false;
    setState({ status: 'loading', visits: [] });
    patientVisitApi.list(patient.id)
      .then(data => { if (!cancelled) setState({ status: 'ready', visits: buildVisitListModel(data) }); })
      .catch(() => { if (!cancelled) setState({ status: 'error', visits: [] }); });
    return () => { cancelled = true; };
  }, [patient?.id, attempt]);

  const seen = patient?.id ? readSeenVisitIds(patient.id) : [];

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Appointments', to: '/patient/appointments' }, { label: 'Visit summaries' }]}>
      <div className="page-shell">
        <AppointmentsTabs />
        <PageHead
          title="Your visit summaries"
          subtitle="What your doctor found, your medicines and your next steps, after each visit."
        />

        {state.status === 'loading' && (
          <div className="skeleton-list" role="status" aria-label="Loading your visits">
            <div className="skeleton skeleton--row" />
            <div className="skeleton skeleton--row" />
            <div className="skeleton skeleton--row" />
          </div>
        )}

        {state.status === 'error' && (
          <div className="alert alert-error" role="alert">
            <span>We couldn&rsquo;t load your visits.</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAttempt(n => n + 1)}>
              Try again
            </button>
          </div>
        )}

        {state.status === 'ready' && state.visits.length === 0 && (
          <EmptyState
            framed
            icon="stethoscope"
            title="No visit summaries yet"
            message="After a doctor visit is finished, your summary shows up here. It lists what the doctor found, your medicines and what to do next."
            action={{ label: 'See my appointments', onClick: () => navigate('/patient/appointments') }}
          />
        )}

        {state.status === 'ready' && state.visits.length > 0 && (
          <div className="card-list">
            {state.visits.map(v => {
              const isNew = isRecentVisit(v.date) && !seen.includes(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  className="card-row"
                  onClick={() => navigate(`/patient/visits/${v.id}`)}
                >
                  <div className="card-row-body">
                    <div className="card-row-title">{v.headline || 'Doctor visit'}</div>
                    <div className="card-row-meta">{v.dateLabel} · {v.doctor}</div>
                  </div>
                  {isNew && <span className="badge badge-active">New</span>}
                  <Icon name="chevronRight" size={14} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </PatientAppShell>
  );
}
